'use client';

import { ApolloProvider } from '@apollo/client';
import { CssBaseline, type PaletteMode, ThemeProvider } from '@mui/material';
import { AppRouterCacheProvider } from '@mui/material-nextjs/v15-appRouter';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import en from 'dayjs/locale/en';
import ru from 'dayjs/locale/ru';
import Cookies from 'js-cookie';
import { type AbstractIntlMessages, NextIntlClientProvider } from 'next-intl';
import { addBasePath } from 'next/dist/client/add-base-path';
import { FC, ReactNode, useMemo, useState } from 'react';
import * as yup from 'yup';

import { COLOR_MODE_COOKIE_NAME } from '../../../constants/theme';
import { defaultTimezone, Locale } from '../../../i18n/routing';
import { useApolloClient } from '../../../lib/apollo-client';
import { setup } from '../../../lib/yup-password';
import { ColorModeContext } from '../../../theme/ColorContext';
import { theme as defaultTheme } from '../../../theme/theme';
import { CommonComponents } from '../../common';

type AllProvidersProps = {
  colorMode: PaletteMode;
  locale: Locale;
  messages: AbstractIntlMessages;
  children?: ReactNode;
};

setup(yup);

en.formats = ru.formats;

export const AllProviders: FC<AllProvidersProps> = (props) => {
  const { colorMode, locale, messages, children } = props;

  const [mode, setMode] = useState(colorMode);

  const apolloClient = useApolloClient();

  const colorModeContext = useMemo(
    () => ({
      toggleColorMode: () => {
        const newMode = mode === 'light' ? 'dark' : 'light';

        setMode(newMode);

        document.body.dataset.theme = newMode;

        Cookies.set(COLOR_MODE_COOKIE_NAME, newMode, {
          path: addBasePath('/'),
        });
      },
    }),
    [mode]
  );

  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (err) {
      return defaultTimezone;
    }
  }, []);

  const theme = useMemo(() => defaultTheme(mode), [mode]);

  return (
    <AppRouterCacheProvider>
      <NextIntlClientProvider locale={locale} timeZone={timezone} messages={messages}>
        <ApolloProvider client={apolloClient}>
          <ColorModeContext.Provider value={colorModeContext}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <LocalizationProvider adapterLocale={locale} dateAdapter={AdapterDayjs}>
                {children}

                <CommonComponents />
              </LocalizationProvider>
            </ThemeProvider>
          </ColorModeContext.Provider>
        </ApolloProvider>
      </NextIntlClientProvider>
    </AppRouterCacheProvider>
  );
};
